/**
 * Helper functions for company onboarding flow
 */

import { TStage } from "../../types/types";
import { addOrEditCompanyOnboardingInitialState } from "../../utils/constants";
import { BASE_URL_DYNAMIC } from "../../services/api/base-query";
import { saveProgress } from "../../utils/onboardingStorage";
import { TCompanyOnboarding } from "../../services/types/users.types";
import { isNewCompanyRecord } from "../../utils/companyProvisioning";

export type SaveCompanyDataResult = {
  mergedData: TCompanyOnboarding;
  isNewCompany: boolean;
};

/**
 * Determine if the proceed button should be enabled based on current step and form state
 */
export const isProceedButtonEnabled = (
  formikProps: any,
  currentStepNumber: number
): boolean => {
  // For step 4 (plan selection), explicitly check if a plan is selected
  if (currentStepNumber === 4) {
    return (
      formikProps.isValid &&
      formikProps.values.selectedPlanId &&
      formikProps.values.selectedPlanId !== ""
    );
  }
  // Use standard validation for all other steps
  return formikProps.isValid;
};

/**
 * Navigate to the previous step
 */
export const getPreviousStep = (
  currentStep: TStage,
  steps: TStage[]
): TStage | undefined => {
  return steps.find((s: TStage) => s.stageNumber === currentStep.stageNumber - 1);
};

/**
 * Navigate to the next step
 */
export const getNextStep = (
  currentStep: TStage,
  steps: TStage[]
): TStage | undefined => {
  return steps.find((s: TStage) => s.stageNumber === currentStep.stageNumber + 1);
};

/**
 * Get a specific step by step number
 */
export const getStepByNumber = (
  stepNumber: number,
  steps: TStage[]
): TStage | undefined => {
  return steps.find((s: TStage) => s.stageNumber === stepNumber);
};

/**
 * Save company data via API
 */
export const saveCompanyData = async (
  values: typeof addOrEditCompanyOnboardingInitialState,
  saveOrUpdateCompanyMutation: any,
  setValues: any,
  onCompanyExists: () => void,
  currentStepNumber: number
): Promise<SaveCompanyDataResult> => {
  const companyData: any = { ...values };
  const isNewCompany = isNewCompanyRecord(companyData.id);

  try {
    const saved = await saveOrUpdateCompanyMutation(companyData).unwrap();

    const mergedData = {
      ...values,
      ...saved, 
      selectedPlanId: saved.selectedPlanId || values.selectedPlanId,
    };
    
    if (saved.selectedPlan && !mergedData.selectedPlanId) {
      mergedData.selectedPlanId = saved.selectedPlan;
    }
    
    // Update form values with the merged data
    setValues(mergedData);
    // Save to localStorage with proper structure
    saveProgress(mergedData, currentStepNumber);

    return { mergedData, isNewCompany };
  } catch (err: any) {
    // Check if the error is COMPANY_EXIST
    if (err?.data?.error === "COMPANY_EXISTS") {
      onCompanyExists();
    }
    throw err;
  }
};

/**
 * Check if in test environment
 */
export const isTestEnvironment = (): boolean => {
  return (
    process.env.NODE_ENV === "test" ||
    BASE_URL_DYNAMIC.toLowerCase().includes("test") ||
    BASE_URL_DYNAMIC.toLowerCase().includes("localhost")
  );
};

/**
 * Initialize form state with saved progress and URL parameters
 */
export const initializeFormState = (
  savedProgress: any,
  selectedPlanId?: string,
  languageId?: string | null
) => {
  const state = { ...addOrEditCompanyOnboardingInitialState };

  // Load saved form values if available
  if (savedProgress?.formValues) {
    Object.assign(state, savedProgress.formValues);
    
    if (savedProgress.formValues.selectedPlan && !savedProgress.formValues.selectedPlanId) {
      state.selectedPlanId = savedProgress.formValues.selectedPlan;
    }
  }

  if (selectedPlanId) {
    state.selectedPlanId = +selectedPlanId;
  }

  if (languageId) {
    state.languageId = +languageId;
  }

  return state;
};

